import { Component, Input, QueryList, Injector, ContentChildren, AfterViewInit, AfterContentInit, HostListener, ElementRef, ViewChild } from '@angular/core';
import { NgModel, CheckboxControlValueAccessor } from '@angular/forms';
import * as moment from 'moment-mini';
import { QuillEditorComponent } from 'ngx-quill';
import { LookupComponent } from '../lookup/lookup.component';
import { GetTextPipe } from '../../pipes';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatDatepickerInput } from '@angular/material/datepicker';
import { MatCheckbox } from '@angular/material/checkbox';

/*
 These components create a standardized form across the application, use <aims-form> to mark a section with a common layout, use <aims-fc> inside the <aims-form> tag to mark a column
 Place the control inside the <aims-fc> tag.  The editable property of the form will override the control and display a text value when the form is not editable
 This all uses Bootstrap under the hood so columns are all sets of 12
 Use the 'fullWidth' class to expand the control to the full width of the space available if desired
 Headers can be inserted on both form and fc by using [header]="'Text Goes Here'"



 IMPORTANT: YOU MUST USE [(ngModel)] AND INSERT #aimsControl="ngModel" ON ALL CONTROLS!!! SEE BELOW

 Example Usage:
 <aims-form [editable]="headerEditable()" [cols]="4">
    <aims-fc>
        <mat-form-field>
            <mat-select #aimsControl="ngModel"
                            [ngModel]="workOrder.tttStatusId"
                            class="fullWidth"
                            name="Status"
                            required>
                    <mat-option *ngFor="let s of tttStatuses | async" [value]="s.tttstatusId">
                        {{ s.status }}
                    </mat-option>
            </mat-select>
        </mat-form-field>
    </aims-fc>
    <aims-fc>
     ...
    </aims-fc>
    <aims-fc>
     ...
    </aims-fc>
    <aims-fc>
     ...
    </aims-fc>
 </aims-form>

 Setting the [cols] value to 1 and labelColSize and controlColSize = 12 will make the labels appear above the control rather than to the left

 Note: *ngIf on the aims-fc appears to break things
 Example:
 <aims-form [editable]="headerEditable()" [cols]="1">
  <aims-fc [labelColSize]="12" [controlColSize]="12">
    <mat-form-field>
        <input matInput #aimsControl="ngModel" class="fullWidth" name="Description" type="text" [(ngModel)]="workOrder.description" />
    </mat-form-field>
  </aims-fc>
</aims-form>


 If you need your column to be laid out differently than the default use optional inputs on the <aims-fc>

Example:
<aims-fc [labelColSize]="6" [controlColSize]="6">
  <input type="checkbox"
          #aimsControl="ngModel"
          name="Requires Lead Approval"
          [(ngModel)]="workOrder.requiresLeadApproval" />
</aims-fc>

Input Values:
<aims-form>
[editable]: optional, set to false to make all controls non-editable and display a text value rather than the control
[cols]: optional, defaults to 4 if you don't override it
[spaceRows]: optional, defaults to true, if false the 10px spacing between the rows is removed
[header]: optional, displays a header across the top of the form
[hider]: optional, if set to true allows the form to be hidden by clicking an eye icon

<aims-fc>
[showDescendants]: optional, boolean, need to flag "false" when layering an aims-form inside of an aims-fc
[labelColSize]: optional, this plus controlColSize should equal 12, defaults to 4 giving a 1:3 ratio of label area to control area, try not to change this too much to keep our forms consistant
[controlColSize]: optional, this plus labelColSize should equal 12, defaults to 8 giving a 1:3 ratio of label area to control area, try not to change this too much to keep our forms consistant
[isSpacer]: optional, will ignore all contents and be a spacer showing an empty space in the form
[spanCols]: optional, by default 1, will stretch aims-fc to take up the entered number of columns in the form, ex: aims-form cols is set to "4" but you have a control you want to take up half the
  avilable space on the form, set [spanCols]="2", can't be higher than the [cols] on the parent aims-form
[editable]: optional, if not set, editable is deterimined by the form, if it is set, this overrides the aims-form setting, ex:  tobe used to keep field not editable even when form is
[noMatAdjust]: optional, boolean, if set then do not apply the material spacer in the case that the form component aboce current one is not a material component.
 */


@Component({
  selector: 'aims-fc',
  styleUrls: ['./aimsForm.component.css'],
  templateUrl: './aimsFormControl.component.html',
  providers: [GetTextPipe]
})
export class AimsFormControlComponent implements AfterContentInit {

  constructor(private inj: Injector,
    private getText: GetTextPipe) {
    this.aimsForm = this.inj.get(AimsFormComponent);
  }

  aimsForm: AimsFormComponent;
  isChild: boolean = false;

  @Input() labelColSize: number = 4;
  @Input() controlColSize: number = 8;
  @Input() isSpacer: boolean;
  @Input() spanCols: number = 1;
  @Input() editable: boolean;
  @Input() showDescendants: boolean = true;
  @Input() noMatAdjust: boolean;

  _showDesc: boolean = false;
  @ContentChildren('aimsControl', {descendants: true}) descChildren !: QueryList<NgModel>;
  @ContentChildren('aimsControl') noDescChildren !: QueryList<NgModel>;
  @ViewChild('wrapper') wrapper: ElementRef;

  children: QueryList<NgModel> = null;

  showFc: boolean = true;
  @HostListener('window:resize', ['$event'])
  resize(event) {
    if (this.isSpacer) {
      if (event.target.innerWidth < 1200) {
        this.showFc = false;
      }
      else {
        this.showFc = true;
      }
    }
  }

  isEditable() {
    return this.aimsForm.editable && (this.editable == undefined || this.editable);
  }

  height() {
    let h = 0;
    if (this.wrapper) {
      h = this.wrapper.nativeElement.height;
    }
    return h;
  }

  classCalc() {
    let retClass = '';

    const cols = this.aimsForm.cols;
    const space: boolean = this.aimsForm.spaceRows;
    const noMatAdjust: boolean = this.noMatAdjust;
    let lgVal = (12 / cols) * this.spanCols;
    if (lgVal > 12) {
      lgVal = 12;
    }
    let mdVal = (12 / cols) * 2;
    (mdVal > 12) ? mdVal = 12 : mdVal = 6;

    const sv = (space || !this.showDescendants) || noMatAdjust ? '' : 'removeMatSpace';
    retClass += `col-xs-12 col-md-${mdVal} col-lg-${lgVal} ${sv}`;


    return retClass;
  }

  firstColSizeCalc() {
    let ret = `col-xs-${this.labelColSize}`;
    if (this.labelColSize != 12) ret += ' text-right';
    return ret;
  }

  isRequired() {
      let c: string = '';
      if (this.wrapper){
        const elRef = this.wrapper.nativeElement;
        if (elRef.innerHTML.includes('required') && elRef.innerHTML.includes('ng-invalid')) {
            c = 'fc-required';
        }
      }

      return c;
  }

  secondColSizeCalc() {
    let ret = `col-xs-${this.controlColSize} fullWidthMatFormField`;
    return ret;
  }

  isCheckbox(child: NgModel) {
    const va = child.valueAccessor;
    return va instanceof CheckboxControlValueAccessor || va instanceof MatCheckbox;
  }

  isAimsLookup(child: NgModel) {
    return child.valueAccessor instanceof LookupComponent;
  }

  isQuillEditor(child: NgModel) {
    const va = child.valueAccessor;
    return va instanceof QuillEditorComponent;
  }

  displayValue(child: NgModel) {
    let ret: string = null;
    if (child.value !== null) {
      const va = child.valueAccessor;
      if (va instanceof MatSelect){
          let ms = (va.selected instanceof Array) ? va.selected[0] as MatOption : va.selected as MatOption;
          ret = (ms) ? ms.viewValue : '';
      }
      else if (va instanceof MatDatepickerInput){
          ret = moment(child.value).format("MM/DD/YYYY");
      }
     else if (va instanceof LookupComponent) {
        ret = va.altDisplay;
        if (va.altDisplay == '') {
          let propArr: Array<string> = (va.displayPropertyName instanceof Array) ? va.displayPropertyName : [<string>va.displayPropertyName];
          propArr.forEach(dispPropName => {
            ret += this.getText.transform(child.value, va.idPropertyName, dispPropName, va.values);
          });
        }
      }
      else {
        ret = child.value;
      }
    }


    return ret;
  }

  ngOnInit() {
    if (this.spanCols > this.aimsForm.cols) {
      this.spanCols = this.aimsForm.cols;
    }
  }

  ngAfterContentInit() {
      this.children = (this.showDescendants) ? this.descChildren : this.noDescChildren;
  }
}



@Component({
  selector: 'aims-form',
  styleUrls: ['./aimsForm.component.css'],
  templateUrl: './aimsForm.component.html'
})
export class AimsFormComponent {

  constructor() {
  }

  @Input() editable: boolean = true;
  @Input() cols: number = 4;
  @Input() spaceRows: boolean = true;
  @Input() header: string;
  @Input() hider: boolean = false;

  showContent: boolean = true;


  @ContentChildren(AimsFormControlComponent) fcs: QueryList<AimsFormControlComponent>;
  maxChildHeight(): number {
    let maxH: number = 45;
    if (this.fcs !== undefined) {
      this.fcs.forEach(fc => {
        const h = fc.height();
        if (h > maxH) {
          maxH = fc.height();
        }
      });
    }
    return maxH;
  };

  ngOnInit() {
  }
}


